import { useCallback, useEffect, useState, MouseEvent } from 'react';
import { useHistory } from 'react-router';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Avatar from '@material-ui/core/Avatar';
import CircularProgress from '@material-ui/core/CircularProgress';
import Hidden from '@material-ui/core/Hidden';
import Skeleton from '@material-ui/lab/Skeleton';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { DateTime } from "luxon";
import { ReservationResponse } from '../../services/models';
import { ReservationService } from '../../services';
import { StatusId } from '../../utils/enums/StatusId';
import handleCantReserve from '../../utils/functions/handleCantReserve';
import snackbarUtils from '../../utils/snackbarUtils';
import formatError from '../../utils/formatters/formatError';

import BlueButton from '../BlueButton';
import FormDialog from '../Schedules/FormDialog';
import CheckOutDialog from '../Schedules/CheckOutDialog';
import { ApartmentIcon, EventIcon } from '../../assets/icons';
import useStyles from './styles';
import useCommonStyles from '../../styles/common';
import { makeFloor } from '../../utils/formatters';
import handleSectionIcon from '../../utils/functions/handleSectionIcon';

interface Props {
  handleAppointmentsCount?: (count: number) => void;
}

const Appointments = ({ handleAppointmentsCount }: Props) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const classes = useStyles();
  const commonClasses = useCommonStyles();

  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [checkOutDialogIsOpen, setCheckOutDialogIsOpen] = useState(false);
  const [formDialogIsOpen, setFormDialogIsOpen] = useState(false);

  const [reservations, setReservations] = useState<ReservationResponse[]>([])

  useEffect(() => {
    async function getReservations() {
      setLoading(true);
      try {
        const { data } = await ReservationService.getReservations();
        if (handleAppointmentsCount) handleAppointmentsCount(data.length);
        setReservations(data)
      } catch (error) {
        console.log('Erro: ', error.message)
        snackbarUtils.error(formatError(error));
      } finally {
        setLoading(false);
      }
    }
    getReservations();
  }, [handleAppointmentsCount, formDialogIsOpen]);

  const handleCheckOutDialog = useCallback(() => setCheckOutDialogIsOpen(value => !value), []);
  const handleFormDialog = useCallback(() => setFormDialogIsOpen(value => !value), []);

  const handleStatusId = useCallback((reservation: ReservationResponse) => {
    const handleCheckIn = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      history.push(`/check-in/${reservation.id}`)
    }
    const handleCheckOut = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handleCheckOutDialog();
    }
    const isToday = () => DateTime.now().hasSame(DateTime.fromISO(reservation.date), "day");

    switch (reservation.statusId) {
      case StatusId.checkIn:
        return (
          isToday() ?
            <Button
              variant='outlined'
              onClick={handleCheckIn}
              className={commonClasses.yellowButton}
            >
              Check In
            </Button>
            :
            null
        )
      case StatusId.checkOut:
        return (
          <Button
            variant='outlined'
            onClick={() => handleRedirect(reservation)}
            className={commonClasses.yellowButton}
          >
            Check Out
          </Button>
        )
      case StatusId.cancelled:
        return (
          <Typography className={classes.cancelled}>Cancelado</Typography>
        )
      default:
        return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewAppointment = useCallback(async () => {
    setIsFetching(true);
    try {
      const { data } = await ReservationService.canReserve();
      !data.can
        ? handleCantReserve(data.reason)
        : history.push('/novo-agendamento');
    } catch (error) {
      console.log(error.message);
      snackbarUtils.error(formatError(error))
    } finally {
      setIsFetching(false);
    }
  }, [history])

  const handleRedirect = useCallback((appointment: ReservationResponse) =>
    history.push(`/meu-agendamento/${appointment.id}`, { appointment }), [history]);

  // Faz com que na página de '/agendamentos', caso não tenha agendamentos, não exiba nada, .
  if (!!handleAppointmentsCount && reservations.length === 0) return null;

  return (
    <Grid container item xs={12} component="section">
      {!handleAppointmentsCount || isMobile ? (
        <Grid item xs={12}>
          <Typography variant="h2" className={classes.title}>
            Últimos Agendamentos
          </Typography>
        </Grid>
      ) : (
        <Grid container item xs={12} className={classes.titleContainer}>
          <Grid item xs={8} md={9}>
            <Typography variant="h2" className={classes.title}>
              Últimos Agendamentos
            </Typography>
          </Grid>

          <Grid container item xs={4} md={3} justify="center">
            <BlueButton
              dataCy="new-appointment-button"
              startIcon={isFetching ? <CircularProgress size="1rem" /> : null}
              action={handleNewAppointment}
              disabled={isFetching}
            >
              Novo Agendamento
            </BlueButton>
          </Grid>
        </Grid>
      )}

      {!loading ? (
        reservations.length !== 0 ? (
          <Box width="100%">
            <Grid container item xs={12} spacing={2}>
              {reservations.map((reservation) => {
                const luxonDate = DateTime.fromISO(reservation.date.toString()).setLocale('pt-BR').toLocaleString();
                return (
                  <Grid container item xs={12} key={reservation.id}>
                    <Grid
                      container
                      item
                      xs={12}
                      className={classes.box}
                    >
                      <Grid
                        container
                        item
                        xs={12}
                        sm={9}
                        spacing={2}
                        component={Button}
                        onClick={() => handleRedirect(reservation)}
                      >
                        <Grid container item xs={3} lg={2} justify="center">
                          <Avatar
                            src={handleSectionIcon(reservation.sectionName)}
                            style={{ background: reservation.color }}
                            className={classes.avatar}
                          />
                        </Grid>

                        <Grid item xs={9}>
                          <Typography variant='subtitle1' className={classes.buildingTitle}>
                            {reservation.buildingTitle}
                          </Typography>

                          <Grid container item xs={9} className={classes.subtitle}>
                            <Grid container item xs={6}>
                              <ApartmentIcon />
                              <Typography variant="subtitle2">{makeFloor(reservation.floor.toString())}</Typography>
                            </Grid>
                            <Grid container item xs={6}>
                              <EventIcon />
                              <Typography variant="subtitle2">{luxonDate}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>

                      </Grid>
                      <Hidden only="xs">
                        <Grid container item xs={3} justify="center">
                          {handleStatusId(reservation)}
                        </Grid>
                      </Hidden>
                    </Grid>

                    <Hidden smUp>
                      <CheckOutDialog
                        isOpen={checkOutDialogIsOpen}
                        handleClose={handleCheckOutDialog}
                        reservationId={reservation.id}
                        handleFormDialog={handleFormDialog}
                      />
                      <FormDialog
                        isOpen={formDialogIsOpen}
                        handleClose={handleFormDialog}
                        reservationId={reservation.id}
                      />
                      <Grid container item xs={12} md={3} className={classes.statusIdComponent}>
                        {handleStatusId(reservation)}
                      </Grid>
                    </Hidden>
                  </Grid>
                )
              }
              )}
            </Grid>
            {(handleAppointmentsCount && isMobile) &&
              <Box width="100%" display="flex" justifyContent="center" marginTop={4}>
                <BlueButton
                  dataCy="new-appointment-button"
                  action={handleNewAppointment}
                  startIcon={isFetching ? <CircularProgress size="1rem" /> : null}
                  disabled={isFetching}
                >
                  Novo Agendamento
                </BlueButton>
              </Box>
            }
          </Box>
        ) : (
          <Grid item xs={12}>
            <Typography variant='subtitle2'>Sem agendamentos</Typography>
          </Grid>
        )) : (
        Array.from(Array(3)).map((i, index) => (
          <Box key={`schedules-skeletons-${index}`} width="100%" marginY={0.5}>
            <Skeleton variant="rect" width="100%" height={74} />
          </Box>
        ))
      )}
    </Grid >
  );
}

export default Appointments;
